import React, { type FC, useState } from 'react'
import { useSelector } from 'react-redux'
import { Drawer } from 'antd'

import { PLEASE_SELECT, USER } from '../../../components/shared/constants'
import { operationDrawerProps, showNotification } from '../../../constants/helpers/common'
import { type IUserPermission } from '../../../constants/types/common'
import { usePostRelationshipsMutation } from '../../../state/apis/generated/relationshipsApi'
import { selectSelectedUser } from '../../../state/slices/appSlice'
import { UsersAvatarBlock, DrawerFooterBlock, CloseDrawerButton } from '../../shared'
import { useGetUsersQuery } from '../../../state/apis/enhancedUsersApi'
import { UserTable } from './UserTable'

interface ICopyPermissionsPanelProps {
  permissions: IUserPermission[]
  dropSelectedPermissions: () => void
  copyPermissionsDrawerVisible: boolean
  setCopyPermissionsDrawerVisible: (arg: boolean) => void
}

export const CopyPermissionsPanel: FC<ICopyPermissionsPanelProps> = ({
  copyPermissionsDrawerVisible,
  permissions,
  dropSelectedPermissions,
  setCopyPermissionsDrawerVisible
}) => {
  const selectedUser = useSelector(selectSelectedUser)
  const [copyRelationships] = usePostRelationshipsMutation()
  const [selectedRowKeys, setSelectedRowKeys] = useState<string[]>([])

  const [searchFilter, setSearchFilter] = useState({})
  const { data: users = [], isLoading: isUsersLoading } = useGetUsersQuery(searchFilter)
  const handleCopyPermissions = async () => {
    const relationships = selectedRowKeys.map(userId => (
      permissions.map(p => ({
        ...p,
        resourceType: `${p.namespace}/${p.resourceType}`,
        userId
      }))
    )).flat()

    const result = await copyRelationships({
      serviceAuthorisationDomainModelsSpiceDbRequestsSpiceDbWriteRelationshipsRequest: {
        relationships
      }
    })

    showNotification({ result, success: `Permissions copied to ${selectedRowKeys.length} user${selectedRowKeys.length === 1 ? '' : 's'}` })
    handleCloseDrawer()
  }

  const handleCloseDrawer = () => {
    setCopyPermissionsDrawerVisible(false)
    setSelectedRowKeys([])
    dropSelectedPermissions()
  }

  return (
    <Drawer
      {...operationDrawerProps}
      title='Copy Permissions'
      data-testid='copy-permissions-drawer'
      onClose={handleCloseDrawer}
      open={copyPermissionsDrawerVisible}
      extra={(
        <CloseDrawerButton onCloseDrawer={handleCloseDrawer} />
      )}
      footer={(
        <DrawerFooterBlock
          confirm={{
            children: 'Copy Permissions',
            disabled: !selectedRowKeys.length,
            onClick: handleCopyPermissions
          }}
          tooltipText={!selectedRowKeys.length ? `${PLEASE_SELECT} ${USER}` : ''} />
      )}
    >
      <UsersAvatarBlock users={[selectedUser]} />
      <p>Select users to copy the permission to.</p>
      <UserTable
        tableData={users}
        loading={isUsersLoading}
        selectedRowKeys={selectedRowKeys}
        setSelectedRowKeys={setSelectedRowKeys}
        searchFilter={searchFilter}
        setSearchFilter={setSearchFilter}
      />
    </Drawer>
  )
}
